import React, {useState, useCallback} from 'react';
import {View, StyleSheet, Platform, TouchableOpacity} from 'react-native';
import {TextInput, HelperText} from 'react-native-paper';
import DateTimePicker from '@react-native-community/datetimepicker';
import {colors, spacing} from '../../theme';

const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

const parseDate = (str) => {
  if (!str || !DATE_REGEX.test(str)) return null;
  const [y, m, d] = str.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  if (date.getFullYear() === y && date.getMonth() === m - 1 && date.getDate() === d) {
    return date;
  }
  return null;
};

const formatDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const DatePickerInput = ({
  label,
  value,
  onChangeText,
  minDate,
  maxDate,
  error,
  style,
}) => {
  const [showPicker, setShowPicker] = useState(false);
  const [touched, setTouched] = useState(false);

  const validationError = useCallback(() => {
    if (!touched || !value) return '';
    if (!DATE_REGEX.test(value)) return 'Use YYYY-MM-DD format';
    const date = parseDate(value);
    if (!date) return 'Invalid date';
    if (minDate) {
      const min = parseDate(minDate);
      if (min && date < min) return `Must be on or after ${minDate}`;
    }
    if (maxDate) {
      const max = parseDate(maxDate);
      if (max && date > max) return `Must be on or before ${maxDate}`;
    }
    return '';
  }, [value, touched, minDate, maxDate]);

  const errorMsg = error || validationError();

  const handlePickerChange = (event, selectedDate) => {
    setShowPicker(false);
    if (event.type === 'dismissed') return;
    if (selectedDate) {
      onChangeText(formatDate(selectedDate));
      setTouched(true);
    }
  };

  const handleTextChange = (text) => {
    onChangeText(text);
    if (!touched) setTouched(true);
  };

  const pickerDate = parseDate(value) || new Date();

  return (
    <View style={style}>
      <TouchableOpacity activeOpacity={1} onPress={() => setShowPicker(true)}>
        <TextInput
          label={label}
          value={value}
          onChangeText={handleTextChange}
          onBlur={() => setTouched(true)}
          mode="outlined"
          style={styles.input}
          placeholder="YYYY-MM-DD"
          outlineColor={errorMsg ? colors.error : colors.border}
          activeOutlineColor={errorMsg ? colors.error : colors.primary}
          keyboardType={Platform.OS === 'ios' ? 'numbers-and-punctuation' : 'numeric'}
          error={!!errorMsg}
          right={<TextInput.Icon icon="calendar" onPress={() => setShowPicker(true)} />}
        />
      </TouchableOpacity>
      {!!errorMsg && (
        <HelperText type="error" visible>{errorMsg}</HelperText>
      )}
      {showPicker && (
        <DateTimePicker
          value={pickerDate}
          mode="date"
          display={Platform.OS === 'ios' ? 'spinner' : 'default'}
          onChange={handlePickerChange}
          minimumDate={minDate ? parseDate(minDate) || undefined : undefined}
          maximumDate={maxDate ? parseDate(maxDate) || undefined : undefined}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  input: {backgroundColor: colors.surface},
});

export default DatePickerInput;
